import { createContext, ReactElement, ReactNode, useCallback, useEffect, useMemo } from 'react'
import { Axios } from 'src/service/axios/axios'
import { useEnvironment } from 'src/env/EnvironmentStore'
import { Utils } from 'src/shared/utils/Utils'
import { useLocalStorage } from 'src/shared/utils/hooks/local-storage-hooks'
import { useNavigateToLogout } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'

interface SessionTimeLeftPromise {
  timeLeft: number
}

interface SessionModalObject {
  typeOfSessionModal: 'warning' | 'expired'
}

interface SessionTimeoutContextValue {
  sessionTimeoutModal: SessionModalObject | null
  manualExtendSession: () => void
  manualLogout: () => void
}

const defaultContextValue: SessionTimeoutContextValue = {
  sessionTimeoutModal: null,
  manualExtendSession: () => {},
  manualLogout: () => {},
}

// modal should be shown 2 minutes before timeout, but we add 20s extra time due to the 20s interval between requests
export const totalCountdownSeconds = 120 // 2m
const showSessionModalPriorOfTimeoutSeconds = totalCountdownSeconds + 20 // 2m20s
const intervalBetweenRequestsMillis = 20000 // 20 s

const SessionTimeoutContext = createContext<SessionTimeoutContextValue>(defaultContextValue)

const SessionTimeoutContextProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [sessionTimeoutModal, setSessionTimeoutModal, clearSessionTimeoutModal] = useLocalStorage<SessionModalObject>(
    'sessionTimeoutModal',
    undefined,
    true,
  )

  const logout = useNavigateToLogout()
  const notificationService = useNotificationService()
  const { backendUrl } = useEnvironment()
  const PUBLIC_CONTEXT = Utils.isPublic()

  const fetchSessionTimeout = useCallback(() => {
    if (sessionTimeoutModal) {
      return
    }

    Axios.getAxios()
      .get<SessionTimeLeftPromise>(`${backendUrl}/api/session-info/session-time-left`)
      .then((res) => {
        const timeLeft = res.data.timeLeft
        if (timeLeft <= showSessionModalPriorOfTimeoutSeconds) {
          setSessionTimeoutModal({ typeOfSessionModal: 'warning' })
        }
      })
      .catch((_) => {})
  }, [sessionTimeoutModal, backendUrl, setSessionTimeoutModal])

  const manualExtendSession = useCallback(() => {
    Axios.getAxios()
      .post(`${backendUrl}/api/session-info/session-extend`)
      .then((_) => {
        clearSessionTimeoutModal()
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
  }, [backendUrl, clearSessionTimeoutModal, notificationService])

  const manualLogout = useCallback(() => {
    setSessionTimeoutModal({ typeOfSessionModal: 'expired' })
    logout()
  }, [logout, setSessionTimeoutModal])

  useEffect(() => {
    let sessionTimeoutInterval: NodeJS.Timeout | null = null
    if (!PUBLIC_CONTEXT) {
      sessionTimeoutInterval = setInterval(fetchSessionTimeout, intervalBetweenRequestsMillis)
    }

    return () => {
      if (sessionTimeoutInterval !== null) {
        clearInterval(sessionTimeoutInterval)
      }
    }
  }, [PUBLIC_CONTEXT, fetchSessionTimeout])

  const contextValue: SessionTimeoutContextValue = useMemo(() => {
    return {
      sessionTimeoutModal: sessionTimeoutModal,
      manualExtendSession: manualExtendSession,
      manualLogout: manualLogout,
    }
  }, [manualExtendSession, sessionTimeoutModal, manualLogout])

  return <SessionTimeoutContext.Provider value={contextValue}>{children}</SessionTimeoutContext.Provider>
}

export { SessionTimeoutContext, SessionTimeoutContextProvider }
